import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext'
import {$getNearestNodeOfType, mergeRegister} from '@lexical/utils'
import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND
} from '@lexical/list'
import {
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND
} from 'lexical'
import {useCallback, useEffect, useState} from 'react'
import {StyledToolbar, StyledToolbarButton, StyledToolbarDivider} from './style'

export const ToolbarPlugin = () => {
    const [editor] = useLexicalComposerContext()
    const [isBold, setIsBold] = useState(false)
    const [isItalic, setIsItalic] = useState(false)
    const [isStrikethrough, setIsStrikethrough] = useState(false)
    const [isCode, setIsCode] = useState(false)
    const [listType, setListType] = useState<string | null>(null)

    const updateToolbar = useCallback(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) return

        setIsBold(selection.hasFormat('bold'))
        setIsItalic(selection.hasFormat('italic'))
        setIsStrikethrough(selection.hasFormat('strikethrough'))
        setIsCode(selection.hasFormat('code'))

        const anchorNode = selection.anchor.getNode()
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
        if ($isListNode(parentList)) {
            setListType(parentList.getListType())
        } else {
            setListType(null)
        }
    }, [])

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => updateToolbar())
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    updateToolbar()
                    return false
                },
                COMMAND_PRIORITY_LOW
            )
        )
    }, [editor, updateToolbar])

    const toggleList = (type: 'bullet' | 'number') => {
        if (listType === type) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
            editor.dispatchCommand(
                type === 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
                undefined
            )
        }
    }

    return (
        <StyledToolbar>
            <StyledToolbarButton
                type="button"
                $active={isBold}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
                title="Grassetto"
            >
                <strong>B</strong>
            </StyledToolbarButton>
            <StyledToolbarButton
                type="button"
                $active={isItalic}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
                title="Corsivo"
            >
                <em>I</em>
            </StyledToolbarButton>
            <StyledToolbarButton
                type="button"
                $active={isStrikethrough}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
                title="Barrato"
            >
                <s>S</s>
            </StyledToolbarButton>
            <StyledToolbarButton
                type="button"
                $active={isCode}
                onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
                title="Codice inline"
            >
                {'</>'}
            </StyledToolbarButton>
            <StyledToolbarDivider />
            <StyledToolbarButton
                type="button"
                $active={listType === 'bullet'}
                onClick={() => toggleList('bullet')}
                title="Lista puntata"
            >
                ≡ Lista
            </StyledToolbarButton>
            <StyledToolbarButton
                type="button"
                $active={listType === 'number'}
                onClick={() => toggleList('number')}
                title="Lista numerata"
            >
                № Lista
            </StyledToolbarButton>
        </StyledToolbar>
    )
}
